import React from 'react';
import { ArrowsClockwise, Package, Tray } from '@phosphor-icons/react';

const STAGE_CHIP = {
  logistics: { cls: 'bg-status-pending-finance/15 text-status-pending-finance', label: 'With Logistics' },
};
function StageChip({ stage }) {
  if (!stage) return <span className="text-[11px] italic text-ink-faint">Not yet collected</span>;
  const cfg = STAGE_CHIP[stage];
  if (!cfg) return <span className="text-[11px] text-ink-faint">{stage}</span>;
  return <span className={`inline-flex rounded-chip px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

// SKU-block summary: "Abraaj  330ML x 20 · 5 Pack" (compact, one line)
function skuSummary(t) {
  const parts = [];
  if (t.product_sku) parts.push(t.product_sku);
  if (t.product_quantity != null) {
    parts.push(t.product_quantity + (t.product_quantity_unit ? ' ' + t.product_quantity_unit : ''));
  }
  return parts.length ? parts.join(' · ') : null;
}

const COLS = 'grid grid-cols-[110px_1fr_130px_1fr_170px_50px] gap-3 items-center';

/**
 * @param {{
 *   tasks: Array<object>, loading: boolean,
 *   onReload: ()=>void, onRowClick: (id:number)=>void,
 * }} props
 */
export default function SampleCollectionsView({ tasks = [], loading = false, onReload, onRowClick }) {
  return (
    <div className="p-1">
      <div className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">

        <div className="flex items-start justify-between border-b border-paper-border p-6">
          <div>
            <h1 className="font-serif text-2xl text-ink flex items-center gap-2">
              <Package size={22} weight="fill" className="text-status-under-process" />
              Sample Collections
            </h1>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              Quality-complaint samples waiting to be collected from the customer.
            </p>
          </div>
          <button
            onClick={onReload}
            title="Refresh"
            className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
            <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Column header */}
        <div className={`${COLS} border-b border-paper-border px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-ink-faint`}>
          <span>Ticket</span>
          <span>Customer</span>
          <span>Area</span>
          <span>Product</span>
          <span>Stage</span>
          <span />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-ink-faint">
            <ArrowsClockwise size={26} className="animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-16 text-center">
            <Tray size={40} className="mx-auto text-ink-faint" />
            <p className="mt-3 font-serif text-lg text-ink">No pending collections</p>
            <p className="mt-1 text-[13px] text-ink-muted">All samples are past the pickup stage.</p>
          </div>
        ) : (
          tasks.map((t, i) => (
            <div
              key={t.id}
              onClick={() => onRowClick?.(t.id)}
              className={`cursor-pointer hover:bg-paper-muted/50 ${i < tasks.length - 1 ? 'border-b border-paper-border' : ''}`}
            >
              <div className={`${COLS} px-6 py-4`}>
                <span className="text-[13px] font-medium text-ink-muted">{t.ticket_number || '—'}</span>
                <span className="min-w-0">
                  {t.customer_name
                    ? <span className="block truncate text-[13px] text-ink" dir="auto">{t.customer_name}</span>
                    : t.customer_mobile
                      ? <span className="block truncate text-[13px] text-ink-muted">{t.customer_mobile}</span>
                      : <span className="text-xs text-ink-faint">—</span>}
                  {t.customer_name && t.customer_mobile && (
                    <span className="block truncate text-[11px] text-ink-faint">{t.customer_mobile}</span>
                  )}
                </span>
                <span className="truncate text-[13px] text-ink-muted" dir="auto">{t.customer_area || '—'}</span>
                <span className="truncate text-[13px] text-ink-muted" dir="auto">{skuSummary(t) || <span className="text-ink-faint">—</span>}</span>
                <span><StageChip stage={t.sample_stage} /></span>
                <span className="text-ink-faint text-xs">→</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
